/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { LicenseSeatData } from "@cosmos/components";
import { getHygraphCosmosPageData } from "@cosmos/hygraph-data";
import { CosmosPageLicenseFragment, Locale } from "@cosmos/hygraph-schema";
import { GetTemplatePageProps, getHygraphLocale, getSlugParam } from "@cosmos/next-common";
import { isError } from "@epic-mw/error-types";
import { notFound } from "next/navigation";

import { getHeroAndSectionsDetails } from "../common/cosmosHeroAndSectionDetails";

import { CosmosPageLicenseClient } from "./CosmosPageLicense.client";

export const getPageData = async (
	locale: string = Locale.En,
	slug: string,
	lang: string,
	previewParam?: string | string[],
): Promise<CosmosPageLicenseFragment> => {
	if (!slug) {
		throw new Error("Missing slug");
	}
	const preview = previewParam && typeof previewParam === "string" ? previewParam : undefined;

	/**
	 * Fetch the parent page data which includes entity data for hero and sections
	 */
	const resp = await getHygraphCosmosPageData(
		"CosmosPageLicenseEntities",
		{
			locale: [locale, Locale.En] as Locale[],
			slug,
		},
		preview,
	);

	const { refSections } = await getHeroAndSectionsDetails(null, resp?.cosmosPageLicense?.refSections, {
		slug,
		locale,
		lang,
		preview,
	});

	const pageData: CosmosPageLicenseFragment = {
		...resp?.cosmosPageLicense,
		id: resp?.cosmosPageLicense?.id || "",
		createdAt: resp?.cosmosPageLicense?.createdAt,
		updatedAt: resp?.cosmosPageLicense?.updatedAt,
		contentLargeImages: resp?.cosmosPageLicense?.contentLargeImages || [],
		mainCards: resp?.cosmosPageLicense?.mainCards || [],
		refSections,
	};

	return pageData;
};

interface GetLicenseTemplateProps extends GetTemplatePageProps {
	seatsData1?: LicenseSeatData;
	seatsData2?: LicenseSeatData;
}

export async function CosmosLicensePage({
	slug: rootSlug = "",
	params,
	searchParams,
	hygraphLocales,
	componentContext,
	...rest
}: GetLicenseTemplateProps): Promise<React.JSX.Element> {
	const slugParam = getSlugParam(params);
	const slug = `${rootSlug}${slugParam}`;

	let pageData: CosmosPageLicenseFragment;
	try {
		pageData = await getPageData(
			getHygraphLocale(hygraphLocales, params.lang),
			slug,
			params.lang,
			searchParams.preview,
		);
		if (!pageData || !pageData.slug) {
			notFound();
		}
	} catch (e) {
		console.error("Failed to get license pageData", { error: isError(e) && e.message });
		notFound();
	}

	return (
		<CosmosPageLicenseClient pageData={pageData} locale={params.lang} componentContext={componentContext} {...rest} />
	);
}
